import * as z from 'zod'
import styled from '@emotion/styled'
import { zodResolver } from '@hookform/resolvers/zod'
import { Reorder } from 'framer-motion'
import React from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { isEmpty } from '@livechat/data-utils'

import { useKeyboardReordering } from '../../../hooks/useKeyboardReordering'
import { useRuleReordering } from '../../../hooks/useRuleReordering'
import FallbackTemplateSection from './FallbackTemplateSection'
import RuleCard from './RuleCard'
import RuleFormHeader from './RulesFormHeader'
import RulesFormSkeleton from './RulesFormSkeleton'
import BottomShade from 'components/BottomShade'
import Form from 'components/fields/Form'
import { Rule } from 'types/rules'
import { Template } from 'types/template'

const FormSchema = z.object({
	reorderedRules: z.array(z.string()),
	fallbackTemplateId: z.string(),
})

export type FormValues = z.infer<typeof FormSchema>

const RulesWrapper = styled.div`
	flex: 1;
	min-height: 0;
	position: relative;
	margin-bottom: ${({ theme }) => theme.spaces.space6};
`

const ScrollContainer = styled.div<{ hideOverflow?: boolean }>`
	height: 100%;
	overflow-y: ${({ hideOverflow }) => (hideOverflow ? 'hidden' : 'auto')};
`

const StyledForm = styled(Form)`
	flex: 1;
	min-height: 0;
	overflow-y: auto;
	padding-bottom: 0;
`

type RulesFormProps = {
	formId: string
	rules: Rule[]
	templates: Template[]
	fallbackTemplateId: string
	onSave: (values: FormValues) => Promise<void>
	onDelete: (id: string) => void
	formRef: React.RefObject<HTMLFormElement>
	onFormStateChange: {
		onHasErrorsChanged: (hasErrors: boolean) => void
		onIsDirtyChanged: (isDirty: boolean) => void
		onIsSubmittingChanged: (isSubmitting: boolean) => void
	}
	highlightedRuleId: string | null
}

function RulesForm({
	formId,
	rules,
	templates,
	fallbackTemplateId,
	onSave,
	onDelete,
	formRef,
	onFormStateChange,
	highlightedRuleId,
}: RulesFormProps) {
	const form = useForm<FormValues>({
		defaultValues: {
			reorderedRules: rules.map((rule) => rule.id),
			fallbackTemplateId,
		},
		mode: 'onTouched',
		resolver: zodResolver(FormSchema),
	})

	const { handleSubmit, reset, setValue, watch, formState } = form
	const { isValid } = formState
	const currentFallbackTemplateId = watch('fallbackTemplateId')

	const { fieldsOrder, setFieldsOrder, handleDragEnd } = useRuleReordering({
		rules,
		setValue,
	})

	const { getKeyDownListener } = useKeyboardReordering({
		fieldsOrder,
		itemsLength: rules.length,
		onReorder: setFieldsOrder,
	})

	React.useEffect(() => {
		onFormStateChange.onHasErrorsChanged(!isValid)
	}, [onFormStateChange, isValid])

	React.useEffect(() => {
		onFormStateChange.onIsDirtyChanged(formState.isDirty)
	}, [onFormStateChange, formState.isDirty])

	React.useEffect(() => {
		onFormStateChange.onIsSubmittingChanged(formState.isSubmitting)
	}, [onFormStateChange, formState.isSubmitting])

	React.useEffect(() => {
		const initialValues = {
			reorderedRules: rules.map((rule) => rule.id),
			fallbackTemplateId,
		}
		reset(initialValues)
	}, [rules, fallbackTemplateId, reset])

	const handleReset = React.useCallback(() => {
		const initialOrder = rules.map((rule) => rule.id)
		reset({
			reorderedRules: initialOrder,
			fallbackTemplateId,
		})
		setFieldsOrder(initialOrder)
	}, [rules, fallbackTemplateId, reset, setFieldsOrder])

	return (
		<FormProvider {...form}>
			<StyledForm id={formId} ref={formRef} onSubmit={handleSubmit(onSave)} onReset={handleReset}>
				<RulesWrapper>
					<ScrollContainer hideOverflow={isEmpty(rules)}>
						<RuleFormHeader allowToAddNewRule={rules.length < 10} />
						{isEmpty(rules) ? (
							<RulesFormSkeleton />
						) : (
							<Reorder.Group axis="y" values={fieldsOrder} onReorder={setFieldsOrder} layoutScroll>
								{fieldsOrder.map((ruleId, index) => {
									const rule = rules.find((rule) => rule.id === ruleId)
									return rule ? (
										<RuleCard
											key={rule.id}
											rule={rule}
											templates={templates}
											onDragEnd={() => handleDragEnd(rule.id)}
											onKeyboardReorder={getKeyDownListener(rule.id)}
											onDelete={onDelete}
											index={index}
											highlighted={rule.id === highlightedRuleId}
										/>
									) : null
								})}
							</Reorder.Group>
						)}
					</ScrollContainer>
					<BottomShade />
				</RulesWrapper>
			</StyledForm>
			<FallbackTemplateSection
				templates={templates}
				value={currentFallbackTemplateId}
				onChange={(value) => setValue('fallbackTemplateId', value, { shouldDirty: true })}
			/>
		</FormProvider>
	)
}

export default RulesForm
